import React, { useEffect, useState } from "react";
import { TrendingUp, Loader2, RefreshCw, Clock, CheckCircle2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "../../lib/ist";

export default function AdminSEORefresh() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState(100);

  const load = async () => {
    try { const { data } = await api.get("/admin/seo/status"); setStatus(data); }
    catch { toast.error("Failed to load SEO status"); }
  };
  useEffect(() => { load(); }, []);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/seo/refresh-now", { limit: Number(limit), wait: false });
      toast.success(`Refresh scheduled · ${data.limit} posts`);
      setTimeout(load, 1500);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="card p-6" data-testid="seo-refresh-card">
      <div className="flex items-center gap-2 text-blue-400 text-xs uppercase tracking-wider"><TrendingUp size={14} /> Programmatic SEO</div>
      <h3 className="text-xl font-bold text-white mt-1" style={{ fontFamily: "Outfit" }}>Daily SEO Refresh</h3>
      <p className="text-slate-400 text-sm mt-1">Rotates through the blog corpus and bumps the 100 oldest posts every 24h. Trigger manually below for an immediate refresh.</p>

      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><Clock size={11} /> Last run</div>
          <div className="text-white text-sm mt-1 font-mono">
            {status?.last_run ? fmtDateTime(status.last_run.started_at) : "Never"}
          </div>
        </div>
        <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><CheckCircle2 size={11} /> Refreshed</div>
          <div className="text-white text-sm mt-1 font-mono">{status?.last_run?.refreshed ?? "—"} posts</div>
        </div>
        <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Duration</div>
          <div className="text-white text-sm mt-1 font-mono">{status?.last_run?.duration_sec ? `${status.last_run.duration_sec}s` : "—"}</div>
        </div>
      </div>

      <div className="mt-4 flex items-end gap-3 flex-wrap">
        <div>
          <label className="label">Batch size</label>
          <input type="number" min="1" max="500" value={limit} onChange={e => setLimit(e.target.value)} className="input w-32" data-testid="seo-limit" />
        </div>
        <button onClick={run} disabled={busy} className="btn-primary inline-flex items-center gap-2" data-testid="seo-run">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh now
        </button>
      </div>

      {status?.recent && status.recent.length > 0 && (
        <div className="mt-5">
          <h4 className="text-white text-sm font-semibold mb-2">Recent runs</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-700">
                <tr>
                  <th className="py-2 text-left">When</th>
                  <th className="text-right">Refreshed</th>
                  <th className="text-right">Skipped</th>
                  <th className="text-right">Duration</th>
                  <th className="text-right pr-1">Source</th>
                </tr>
              </thead>
              <tbody>
                {status.recent.map((r, i) => (
                  <tr key={i} className="border-b border-slate-800">
                    <td className="py-1.5 text-slate-300">{fmtDateTime(r.started_at)}</td>
                    <td className="text-right text-emerald-300 font-mono">{r.refreshed}</td>
                    <td className="text-right text-rose-300 font-mono">{r.skipped || 0}</td>
                    <td className="text-right text-slate-400 font-mono">{r.duration_sec}s</td>
                    <td className="text-right text-slate-400 pr-1">{r.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
